import { useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { ShieldCheck, QrCode, Check } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export default function Aff2FASetup({ enabled, onUpdate }) {
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const setupTOTP = async () => {
    try {
      const res = await axios.post(`${AFF_API}/affiliate/2fa/setup`, {}, { headers: getAffHeaders() });
      setQrCode(res.data.qr_code); setSecret(res.data.secret);
    } catch { toast.error("Setup failed"); }
  };

  const verify = async () => {
    if (!code || code.length !== 6) { toast.error("Enter 6-digit code"); return; }
    setVerifying(true);
    try {
      await axios.post(`${AFF_API}/affiliate/2fa/verify`, { code }, { headers: getAffHeaders() });
      toast.success("2FA enabled!"); setQrCode(null); setCode(""); onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setVerifying(false); }
  };

  return (
    <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md p-5 h-full" data-testid="aff-2fa-card">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={18} weight="duotone" className="text-blue-500" />
        <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>2FA Security</h3>
      </div>

      {enabled ? (
        <div className="text-center py-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
            <Check size={24} weight="bold" className="text-emerald-500" />
          </div>
          <p className="text-sm text-emerald-400 font-medium">2FA Enabled</p>
          <p className="text-[10px] text-zinc-500 mt-1">Your account is protected</p>
        </div>
      ) : qrCode ? (
        <div className="space-y-3">
          <div className="flex justify-center p-3 rounded-lg bg-white">
            <img src={qrCode} alt="QR" className="w-28 h-28" data-testid="aff-2fa-qr" />
          </div>
          <p className="text-[9px] text-zinc-500 text-center break-all">Secret: <code className="text-blue-400">{secret}</code></p>
          <Input data-testid="aff-2fa-code-input" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-center text-lg tracking-[6px] font-mono focus:ring-1 focus:ring-blue-500" />
          <button onClick={verify} disabled={verifying} data-testid="aff-2fa-verify-btn" className="w-full py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all disabled:opacity-50">
            {verifying ? "Verifying..." : "Verify & Enable"}
          </button>
        </div>
      ) : (
        <div className="text-center py-2">
          <p className="text-xs text-zinc-400 mb-3">Scan QR with Google Authenticator</p>
          <button onClick={setupTOTP} data-testid="aff-setup-2fa-btn" className="px-4 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 hover:shadow-[0_0_15px_rgba(59,130,246,0.5)] transition-all flex items-center gap-1.5 mx-auto">
            <QrCode size={14} /> Setup 2FA
          </button>
        </div>
      )}
    </div>
  );
}
